import React, { useState, useEffect, useCallback } from 'react';
import { PaperAirplaneIcon, InstallIcon, XMarkIcon, IosShareIcon } from './Icons';

const InstallPwaPrompt: React.FC = () => {
    const [installPrompt, setInstallPrompt] = useState<any>(null);
    const [showStandardPrompt, setShowStandardPrompt] = useState(false);
    const [showIosPrompt, setShowIosPrompt] = useState(false);

    useEffect(() => {
        // --- Standard PWA Install Prompt (for Chrome, Edge, etc.) ---
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setInstallPrompt(e);
            // Check if not standalone and not on iOS before showing standard prompt
            if (!window.matchMedia('(display-mode: standalone)').matches && !/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                setShowStandardPrompt(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // --- iOS Specific Install Instructions ---
        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        const hasDismissedIos = localStorage.getItem('hasDismissedIosInstallPrompt');

        // Show prompt if on iOS, not installed, and not previously dismissed
        if (isIos && !isStandalone && !hasDismissedIos) {
            // Wait a moment before showing to not be too intrusive
            const timer = setTimeout(() => {
                setShowIosPrompt(true);
            }, 3000); // Show after 3 seconds
            
            return () => {
                clearTimeout(timer);
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }

        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    const handleInstallClick = useCallback(() => {
        if (!installPrompt) return;
        installPrompt.prompt();
        installPrompt.userChoice.then((choiceResult: { outcome: string }) => {
            if (choiceResult.outcome === 'accepted') {
                console.log('User accepted the install prompt');
            } else {
                console.log('User dismissed the install prompt');
            }
            setShowStandardPrompt(false);
            setInstallPrompt(null);
        });
    }, [installPrompt]);
    
    const handleDismissStandard = useCallback(() => {
        setShowStandardPrompt(false);
    }, []);
    
    const handleDismissIos = useCallback(() => {
        localStorage.setItem('hasDismissedIosInstallPrompt', 'true');
        setShowIosPrompt(false);
    }, []);

    // Render logic for the standard prompt (Chrome, Edge on Android/Desktop)
    if (showStandardPrompt && installPrompt) {
        return (
            <div role="dialog" aria-labelledby="pwa-install-banner" className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up-fade-in no-print">
                <div className="container mx-auto max-w-2xl bg-slate-800/90 backdrop-blur-md text-slate-100 rounded-xl shadow-2xl p-4 border border-slate-700 flex items-center gap-4">
                    <PaperAirplaneIcon className="w-10 h-10 text-blue-500 -rotate-45 flex-shrink-0" />
                    <div className="flex-grow">
                        <h3 id="pwa-install-banner" className="font-bold">Get the full app experience</h3>
                        <p className="text-sm text-slate-300">Install JT-Crew Movement for quick access and offline use.</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <button onClick={handleDismissStandard} className="p-2 text-slate-400 hover:text-white transition-colors" aria-label="Dismiss install prompt">
                           <XMarkIcon className="w-5 h-5" />
                        </button>
                        <button onClick={handleInstallClick} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-2 transition-colors duration-200">
                            <InstallIcon className="w-5 h-5"/>
                            Install
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Render logic for the iOS-specific instructions
    if (showIosPrompt) {
        return (
             <div role="dialog" aria-labelledby="pwa-ios-install-banner" className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up-fade-in no-print">
                <div className="container mx-auto max-w-2xl bg-slate-800/90 backdrop-blur-md text-slate-100 rounded-xl shadow-2xl p-4 border border-slate-700 flex items-center gap-4">
                    <PaperAirplaneIcon className="w-10 h-10 text-blue-500 -rotate-45 flex-shrink-0" />
                    <div className="flex-grow">
                        <h3 id="pwa-ios-install-banner" className="font-bold">Install on your iPhone/iPad</h3>
                        <p className="text-sm text-slate-300">
                            For the best experience, tap the Share icon <IosShareIcon className="w-5 h-5 inline-block mx-1 -mt-1" /> then 'Add to Home Screen'.
                        </p>
                    </div>
                    <div className="flex-shrink-0">
                        <button onClick={handleDismissIos} className="p-2 text-slate-400 hover:text-white transition-colors" aria-label="Dismiss install instructions">
                           <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default InstallPwaPrompt;